import { useSocialLinks } from 'app/hooks/use-social-links';
import {
  IconAlertTriangle,
  IconSend2,
  IconShare,
  IconUserMinus,
  IconUserPlus,
  IconUserSearch,
} from '@tabler/icons-react-native';
import { userInfoAtom } from 'app/atoms';
import { ShowErrors } from 'app/components/show-errors';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Button, PressableType } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useAuthorize } from 'app/hooks/use-authorize';
import { ChannelType, SUPPORT_CHANNEL_URL, useCreateChannel } from 'app/network/chat/channel';
import { useAddTrustedMember, useProfile, useRemoveTrustedMember } from 'app/network/profile';
import { useReportUser } from 'app/network/user';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import type { NavigationScreen } from 'app/types/navigation';
import type { ProfileShare } from 'app/types/profile';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useAtomValue } from 'jotai';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shareNeighbourProfile } from './components/shared';
import { theme } from './components/theme';

type Props = {
  member: ProfileShare['counterpart'];
  navigation: React.ComponentProps<NavigationScreen<'profile-shares'>>['navigation'];
  onClose: () => void;
};

export function ShareMemberActionsSheet({ member, navigation, onClose }: Props) {
  const { t } = useTranslation('neighbour-profile');
  const authorize = useAuthorize();
  const viewer = useAtomValue(userInfoAtom);
  const isSelf = viewer?.id === member.userId;
  const trust = useMemberTrustAction(member.userId, authorize);
  const report = useReportUser();
  const channel = useCreateChannel(ChannelType.Member, {
    onSuccess: (result) => closeThen(() => navigateToChat(navigation, result.channel_url)),
  });
  const [confirmReport, setConfirmReport] = useState(false);
  const { open, closeThen, onDismiss } = useDeferredSheetClose(onClose);
  const busy = trust.isPending || report.isPending || channel.isPending;
  const actions = confirmReport
    ? [
        {
          label: t('shares.memberActions.report'),
          Icon: IconAlertTriangle,
          onPress: () =>
            authorize(() =>
              report.mutate(member.userId, {
                onSuccess: () =>
                  closeThen(() =>
                    navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
                      i18nKey: 'reportUserPrompt',
                      ns: 'neighbour-profile',
                    }),
                  ),
              }),
            ),
        },
        { label: t('trust.cancel'), Icon: undefined, onPress: () => setConfirmReport(false) },
      ]
    : [
        {
          label: t('shares.memberActions.view'),
          Icon: IconUserSearch,
          onPress: () => closeThen(() => navigateToUserProfile(navigation, member.userId)),
        },
        ...(!isSelf
          ? [
              {
                label: t('shares.memberActions.message'),
                Icon: IconSend2,
                onPress: () => authorize(() => channel.mutate({ id: member.userId })),
              },
              ...trust.actions,
            ]
          : []),
        {
          label: t('shareProfile'),
          Icon: IconShare,
          onPress: () =>
            closeThen(() => {
              void shareNeighbourProfile(member, t);
            }),
        },
        ...(!isSelf
          ? [
              {
                label: t('shares.memberActions.report'),
                Icon: IconAlertTriangle,
                onPress: () => setConfirmReport(true),
              },
            ]
          : []),
      ];
  return (
    <BottomSheet
      open={open}
      onClose={onDismiss}
      containerStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
      indicatorStyle={{
        width: 64,
        height: 4,
        marginTop: 6,
        marginBottom: 6,
        backgroundColor: theme.colors['text.tertiary'],
      }}
      contentStyle={{ paddingHorizontal: 24, paddingTop: 8, paddingBottom: 16 }}
    >
      {actions.map(({ label, Icon, onPress, ...action }) => (
        <Button
          key={label}
          accessibilityRole="button"
          accessibilityLabel={label}
          disabled={busy || ('disabled' in action && action.disabled)}
          onPress={onPress}
          pressableType={PressableType.GestureHandler}
          variant="plain"
          style={{
            minHeight: 56,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 16,
            paddingVertical: 8,
          }}
        >
          {Icon ? <Icon size={24} strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} /> : null}
          <Text variant="bodyBase" fontWeight="600" style={{ flex: 1 }}>
            {label}
          </Text>
        </Button>
      ))}
      <ShowErrors error={trust.profileError ?? channel.error ?? trust.mutationError ?? report.error} />
    </BottomSheet>
  );
}

function useMemberTrustAction(userId: string, authorize: ReturnType<typeof useAuthorize>) {
  const { t } = useTranslation('neighbour-profile');
  const socialLinks = useSocialLinks();
  const profile = useProfile(userId);
  const addTrust = useAddTrustedMember();
  const removeTrust = useRemoveTrustedMember();
  const isTrusted = profile.data?.trust?.isTrustedByViewer ?? false;
  return {
    profileError: profile.error,
    mutationError: addTrust.error ?? removeTrust.error,
    isPending: addTrust.isPending || removeTrust.isPending,
    actions: socialLinks.enabled
      ? [
          {
            label: t(isTrusted ? 'shares.memberActions.removeTrust' : 'shares.memberActions.addTrust'),
            Icon: isTrusted ? IconUserMinus : IconUserPlus,
            disabled: !profile.data || profile.isFetching,
            onPress: () => authorize(() => (isTrusted ? removeTrust.mutate(userId) : addTrust.mutate(userId))),
          },
        ]
      : [],
  };
}

/** Deferred work is event-only: it must run after the native sheet finishes closing. */
function useDeferredSheetClose(onClose: () => void) {
  const [open, setOpen] = useState(true);
  const afterClose = useRef<(() => void) | null>(null);
  function closeThen(action: () => void) {
    afterClose.current = action;
    setOpen(false);
  }
  function onDismiss() {
    const action = afterClose.current;
    afterClose.current = null;
    onClose();
    action?.();
  }
  return { open, closeThen, onDismiss };
}
